import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentUpdateRow, UpdateListDTO } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import BuscadorHorarios, { FiltroCasillas, HorarioFila } from "../../../../components/horarios/buscadorHorarios";
import S from "./strings";
import { DeshacerReparto, auditoriaReparto, useEsCaja } from "./piezas";
import { Preset, TabProps, TYPE_LABELS, TYPE_ORDER, errorText, fechaCorta, nombreHorario, rangoPreset } from "./helpers";

/** "Sáb 3:00 p.m. → Dom 7:00 a.m." y lo que explica el cambio (extra, nota). */
export const detalleDe = (r: EnrollmentUpdateRow): string => {
	const parts: string[] = [];
	const to = r.toCourse ? `${r.toCourse.name}${r.toSchedule ? ", " + nombreHorario(r.toSchedule.name) : ""}` : nombreHorario(r.toSchedule?.name);
	if (r.fromSchedule?.name || to) { parts.push(`${nombreHorario(r.fromSchedule?.name) || "—"}${to ? " → " + to : ""}`); }
	if (r.extraSessionsOption) { parts.push(S.extraOptions.find((o) => o.id === r.extraSessionsOption)?.t || ""); }
	if (r.statusId === 2 && r.returnReason) { parts.push(`${S.fixReason}: ${r.returnReason}`); }
	if (r.note) { parts.push(r.note.length > 90 ? r.note.slice(0, 90) + "…" : r.note); }
	return parts.filter(Boolean).join(". ");
};

/** Motivo de un rechazo: viene en la fila si el servidor lo manda; si no, se lee de los comentarios al mostrar la fila. */
const Motivo: React.FC<{ campusId: number; row: EnrollmentUpdateRow }> = ({ campusId, row }) => {
	const [texto, setTexto] = useState<string | null>(row.rejectReason ?? null);
	const [error, setError] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	useEffect(() => {
		if (texto !== null) { return; }
		let vigente = true;
		HttpManager.getInstance().enrollmentUpdatesService.details(campusId, row.id).then((r) => {
			if (!vigente) { return; }
			const c = [...(r.data.comments || [])].reverse().find((x) => /^(Descartada|Rechazada)/.test(x.text || ""));
			setTexto(c ? c.text.replace(/^(Descartada|Rechazada):?\s*/, "").replace(/\.$/, "") : "");
		}).catch((e) => vigente && setError(errorText(e)));
		return () => { vigente = false; };
	}, [row.id, intento]);
	if (texto !== null) { return <span className="wau-oro small">{S.rejection}: {texto || "—"}</span>; }
	return error ? <button type="button" className="wau-btn s mini" onClick={() => { setError(null); setIntento(intento + 1); }}>{S.seeReason}</button> : <span className="wau-sec small">{S.loading}</span>;
};

const clase = (r: EnrollmentUpdateRow): string => r.statusId === 3 ? "" : " wau-oro";
const pill = (r: EnrollmentUpdateRow) => <span className={"wau-pastilla" + (r.statusId === 3 ? " acento" : r.statusId === 2 || r.statusId === 4 ? " oro" : "")}>{TYPE_LABELS[r.typeId || 0] || r.typeLabel || "—"}</span>;
const cuando = (r: EnrollmentUpdateRow) => fechaCorta(r.eventAt || r.appliedAt || r.requestedAt);

/** Lo que tenía de descuento la matrícula vieja de un cambio de curso aplicado. */
const previo = (r: EnrollmentUpdateRow) => r.statusId === 3 && r.result?.previousAgreement?.text ? <div className="wau-sec small">{r.result.previousAgreement.text}</div> : null;

/** Lista de actualizaciones: tabla en pantalla grande, tarjetas en teléfono. */
export const HistorialLista: React.FC<{ campusId: number; countryId?: number | null; rows: EnrollmentUpdateRow[]; movil: boolean; onChanged?: (msg: string) => void; onError?: (msg: string) => void }> = ({ campusId, countryId, rows, movil, onChanged, onError }) => {
	const [deshechos, setDeshechos] = useState<number[]>([]);
	const conReparto = rows.find((r) => auditoriaReparto(r) !== null);
	const esCaja = useEsCaja(campusId, conReparto ? conReparto.enrollmentId : null);
	const deshacer = (r: EnrollmentUpdateRow) => {
		const audit = auditoriaReparto(r);
		return esCaja && audit !== null && !deshechos.includes(audit) ? (
			<div style={{ marginTop: 4 }}><DeshacerReparto campusId={campusId} countryId={countryId} auditItemId={audit} studentName={r.studentName}
				onDone={(m) => { setDeshechos((d) => [...d, audit]); if (onChanged) { onChanged(m); } }} onError={(m) => { if (onError) { onError(m); } }} /></div>
		) : null;
	};
	if (rows.length === 0) { return <div className="wau-caja wau-sec">{S.empty}</div>; }
	if (movil) {
		return <div>{rows.map((r) => (
			<div className="wau-tarjeta" key={"l" + r.id}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><b>{r.studentName}</b>{pill(r)}</div>
				<div className={"linea wau-num" + clase(r)}>{cuando(r)}, {S.statusLabels[r.statusId]}</div>
				<div style={{ fontSize: 13.5 }}>{detalleDe(r)}</div>
				{r.statusId === 4 ? <div style={{ marginTop: 4 }}><Motivo campusId={campusId} row={r} /></div> : null}
				{previo(r)}
				{deshacer(r)}
				<div className="linea">{r.requestedBy || "—"}{r.appliedBy ? ", " + r.appliedBy : ""}</div>
			</div>
		))}</div>;
	}
	return (
		<div className="wau-tabla">
			<table>
				<thead><tr><th>{S.col.date}</th><th>{S.col.student}</th><th>{S.col.update}</th><th>{S.col.detail}</th><th>{S.col.requestedBy}</th><th>{S.col.appliedBy}</th></tr></thead>
				<tbody className="wau-num">
					{rows.map((r) => (
						<tr key={"l" + r.id}>
							<td style={{ whiteSpace: "nowrap" }}>{cuando(r)}</td>
							<td><b>{r.studentName}</b>{r.courseName ? <div className="wau-sec small">{cursoCorto(r.courseName) || r.courseName}</div> : null}</td>
							<td style={{ whiteSpace: "nowrap" }}>{pill(r)}<div className={"small" + (r.statusId === 3 ? " wau-sec" : " wau-oro")}>{S.statusLabels[r.statusId]}</div></td>
							<td>{detalleDe(r)}{r.statusId === 4 ? <div><Motivo campusId={campusId} row={r} /></div> : null}{previo(r)}{deshacer(r)}</td>
							<td>{r.requestedBy || "—"}</td>
							<td>{r.appliedBy || "—"}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const ESTADOS = [1, 2, 3, 4];

/** Historial: todo lo pedido, aplicado y rechazado, con filtros de fecha, tipo, estado, quién y horario. */
const HistorialTab: React.FC<TabProps> = ({ campusId, countryId, movil, notify, changed }) => {
	const http = HttpManager.getInstance();
	const [preset, setPreset] = useState<Preset>("semana");
	const [tipo, setTipo] = useState<string[]>([]);
	const [estado, setEstado] = useState<string[]>([]);
	const [pidio, setPidio] = useState<string[]>([]);
	const [aplico, setAplico] = useState<string[]>([]);
	const [horario, setHorario] = useState<HorarioFila | null>(null);
	const [eligiendo, setEligiendo] = useState<boolean>(false);
	const [q, setQ] = useState<string>("");
	const [page, setPage] = useState<number>(1);
	const [result, setResult] = useState<UpdateListDTO | null>(null);

	useEffect(() => {
		let vigente = true;
		http.enrollmentUpdatesService.log(campusId, { ...rangoPreset(preset), typeId: Number(tipo[0]) || null, statusId: estado.length ? estado.join(",") : null,
			requestedBy: Number(pidio[0]) || null, appliedBy: Number(aplico[0]) || null, scheduleId: horario?.id ?? null, page })
			.then((r) => vigente && setResult(r.data)).catch((e) => vigente && notify(errorText(e), false));
		return () => { vigente = false; };
	}, [campusId, preset, tipo, estado, pidio, aplico, horario, page]);

	const cambia = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };
	const rows = useMemo(() => {
		const aguja = q.trim().toLowerCase();
		const todas = result?.data || [];
		if (!aguja) { return todas; }
		return todas.filter((r) => [r.studentName, r.courseName, r.fromSchedule?.name, r.toSchedule?.name, r.toCourse?.name, r.note, r.requestedBy, r.appliedBy].join(" ").toLowerCase().includes(aguja));
	}, [result, q]);

	const presets: Preset[] = ["hoy", "semana", "mes", "todo"];
	return (
		<div className="wau-pila">
			<div className="wau-fila" style={{ alignItems: "center" }}>
				<div className="wau-seg" role="group">
					{presets.map((p) => <button key={p} type="button" aria-pressed={preset === p} onClick={() => { setPreset(p); setPage(1); }}>{S.presets[p]}</button>)}
				</div>
				<div className="wau-chips" style={{ marginBottom: 0 }}>
					<FiltroCasillas unico etiqueta={S.filterType} opciones={TYPE_ORDER.map((id) => ({ v: String(id), t: TYPE_LABELS[id] }))} elegidos={tipo} onCambio={cambia(setTipo)} />
					<FiltroCasillas etiqueta={S.statusFilter} opciones={ESTADOS.map((id) => ({ v: String(id), t: S.statusLabels[id] }))} elegidos={estado} onCambio={cambia(setEstado)} />
					<FiltroCasillas unico etiqueta={S.filterRequester} opciones={(result?.requesters || []).map((p) => ({ v: String(p.id), t: p.name }))} elegidos={pidio} onCambio={cambia(setPidio)} />
					<FiltroCasillas unico etiqueta={S.appliedByFilter} opciones={(result?.appliers || []).map((p) => ({ v: String(p.id), t: p.name }))} elegidos={aplico} onCambio={cambia(setAplico)} />
					<span className="wbh-filtro">
						<button type="button" className={"wbh-chip" + (horario ? " on" : "")} onClick={() => setEligiendo(true)}>{S.filterSchedule}{horario ? <b>{horario.horario}</b> : <span className="caret" aria-hidden="true">▾</span>}</button>
						{horario ? <button type="button" className="wbh-x" aria-label={"Quitar " + S.filterSchedule} onClick={() => { setHorario(null); setPage(1); }}>×</button> : null}
					</span>
				</div>
				<div className="wau-buscar grow">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
					<input value={q} placeholder={S.searchLog} aria-label={S.searchLog} onChange={(e) => setQ(e.target.value)} />
				</div>
			</div>
			{result === null ? <div className="wau-caja wau-sec">{S.loading}</div> : <HistorialLista campusId={campusId} countryId={countryId} rows={rows} movil={movil} onChanged={(m) => { notify(m); changed(); }} onError={(m) => notify(m, false)} />}
			{result && result.lastPage > 1 ? (
				<div className="wau-acciones" style={{ justifyContent: "center" }}>
					<button type="button" className="wau-btn s mini" disabled={page <= 1} aria-label="Anterior" onClick={() => setPage(page - 1)}>←</button>
					<span className="wau-num wau-sec">{page} / {result.lastPage}</span>
					<button type="button" className="wau-btn s mini" disabled={page >= result.lastPage} aria-label="Siguiente" onClick={() => setPage(page + 1)}>→</button>
				</div>
			) : null}
			{eligiendo ? (
				<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={() => setEligiendo(false)}>
					<div className="wau-modal ancho" onClick={(e) => e.stopPropagation()}>
						<h3>{S.filterSchedule}</h3>
						<BuscadorHorarios campusId={campusId} autoFocus seleccionado={horario?.id} onElegir={(h) => { setHorario(h); setPage(1); setEligiendo(false); }} />
						<div className="wau-acciones"><button type="button" className="wau-btn s" onClick={() => setEligiendo(false)}>{S.cancel}</button></div>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default HistorialTab;
